import React, { useState } from 'react';
import { TableRow } from './TableRow';

export function TableBody({
    processedData,
    columns,
    pkCol,
    fks,
    fkOptions,
    isPolymorphicTable,
    polymorphicLabels,
    editingRowPk,
    handleEditClick,
    handleDeleteClick,
    handleSaveClick,
    handleCancelClick,
    schemaWarning,
    isAdding,
    editFormData,
    handleInputChange,
    uniqueSchemas,
    tablesList,
    loadingPolyRecords,
    polymorphicRecords,
    groupByConfig
}) {
    const visibleColumns = columns.filter(col => !groupByConfig.includes(col.ColumnName));

    const renderAddRow = () => {
        if (!isAdding) return null;
        return (
            <tr className="border-b border-slate-600 bg-slate-700/50">
                <td className="px-4 py-3 font-medium whitespace-nowrap">
                    <div className="flex gap-2">
                        <button onClick={handleSaveClick} className="text-green-400 hover:text-green-300 font-bold">Save</button>
                        <button onClick={handleCancelClick} className="text-slate-400 hover:text-slate-200">Cancel</button>
                    </div>
                </td>
                {visibleColumns.map(col => {
                    const colName = col.ColumnName;
                    if (col.IsIdentity) {
                        return <td key={colName} className="px-4 py-2 text-slate-500 italic">Auto-generated</td>;
                    }
                    
                    if (isPolymorphicTable && colName === 'TargetSchema') {
                        return (
                            <td key={colName} className="px-4 py-2">
                                <select
                                    value={editFormData.TargetSchema || ''}
                                    onChange={(e) => handleInputChange(e, 'TargetSchema')}
                                    className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2"
                                >
                                    <option value="">-- Select Schema --</option>
                                    {uniqueSchemas.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </td>
                        );
                    }

                    if (isPolymorphicTable && colName === 'TargetTable') {
                        const matchingTables = tablesList
                            .filter(t => t.startsWith(`${editFormData.TargetSchema}.`))
                            .map(t => t.split('.')[1]);

                        return (
                            <td key={colName} className="px-4 py-2">
                                <select
                                    value={editFormData.TargetTable || ''}
                                    disabled={!editFormData.TargetSchema}
                                    onChange={(e) => handleInputChange(e, 'TargetTable')}
                                    className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2 disabled:opacity-40"
                                >
                                    <option value="">-- Select Table --</option>
                                    {matchingTables.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </td>
                        );
                    }

                    if (isPolymorphicTable && colName === 'TargetRecordID') {
                        return (
                            <td key={colName} className="px-4 py-2">
                                <select
                                    value={editFormData.TargetRecordID || ''}
                                    disabled={!editFormData.TargetTable || loadingPolyRecords}
                                    onChange={(e) => handleInputChange(e, 'TargetRecordID')}
                                    className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2 disabled:opacity-40"
                                >
                                    <option value="">{loadingPolyRecords ? "Fetching records..." : "-- Select Target Record --"}</option>
                                    {polymorphicRecords.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </td>
                        );
                    }

                    const isFk = fks.some(fk => fk.ColumnName === colName);
                    if (isFk && fkOptions[colName]) {
                        return (
                            <td key={colName} className="px-4 py-2">
                                <select
                                    value={editFormData[colName] || ''}
                                    onChange={(e) => handleInputChange(e, colName)}
                                    className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2"
                                >
                                    <option value="">-- Select --</option>
                                    {fkOptions[colName].options.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </td>
                        );
                    }
                    return (
                        <td key={colName} className="px-4 py-2">
                            <input
                                type="text"
                                value={editFormData[colName] ?? ''}
                                onChange={(e) => handleInputChange(e, colName)}
                                className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2"
                            />
                        </td>
                    );
                })}
            </tr>
        );
    };

    // Recursive grouping renderer
    const renderRowsOrGroups = (dataNode, depth = 0) => {
        if (Array.isArray(dataNode)) {
            // It's a flat array of rows
            if (dataNode.length === 0 && !isAdding) {
                return (
                    <tr>
                        <td colSpan={visibleColumns.length + 1} className="p-4 text-center text-slate-500">No records found.</td>
                    </tr>
                );
            }
            return dataNode.map((row, idx) => {
                const rowKey = pkCol ? row[pkCol.ColumnName] : idx;
                
                // If editing, render edit form inline
                if (pkCol && row[pkCol.ColumnName] === editingRowPk) {
                    return (
                        <tr key={rowKey} className="border-b border-slate-600 bg-slate-700/50">
                            <td className="px-4 py-3 font-medium whitespace-nowrap">
                                <div className="flex gap-2">
                                    <button onClick={() => handleSaveClick(true)} className="text-green-400 hover:text-green-300 font-bold">Save</button>
                                    <button onClick={handleCancelClick} className="text-slate-400 hover:text-slate-200">Cancel</button>
                                </div>
                            </td>
                            {visibleColumns.map(col => {
                                const colName = col.ColumnName;
                                if (col.IsIdentity || col.IsPrimaryKey) {
                                    return <td key={colName} className="px-4 py-2 text-slate-500 italic">{row[colName]}</td>;
                                }

                                if (isPolymorphicTable && colName === 'TargetSchema') {
                                    return (
                                        <td key={colName} className="px-4 py-2">
                                            <select value={editFormData.TargetSchema || ''} onChange={(e) => handleInputChange(e, 'TargetSchema')} className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2">
                                                <option value="">-- Select Schema --</option>
                                                {uniqueSchemas.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </td>
                                    );
                                }
                                if (isPolymorphicTable && colName === 'TargetTable') {
                                    const matchingTables = tablesList.filter(t => t.startsWith(`${editFormData.TargetSchema}.`)).map(t => t.split('.')[1]);
                                    return (
                                        <td key={colName} className="px-4 py-2">
                                            <select value={editFormData.TargetTable || ''} disabled={!editFormData.TargetSchema} onChange={(e) => handleInputChange(e, 'TargetTable')} className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2 disabled:opacity-40">
                                                <option value="">-- Select Table --</option>
                                                {matchingTables.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </td>
                                    );
                                }
                                if (isPolymorphicTable && colName === 'TargetRecordID') {
                                    return (
                                        <td key={colName} className="px-4 py-2">
                                            <select value={editFormData.TargetRecordID || ''} disabled={!editFormData.TargetTable || loadingPolyRecords} onChange={(e) => handleInputChange(e, 'TargetRecordID')} className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2 disabled:opacity-40">
                                                <option value="">{loadingPolyRecords ? "Fetching records..." : "-- Select Target Record --"}</option>
                                                {polymorphicRecords.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </td>
                                    );
                                }

                                const isFk = fks.some(fk => fk.ColumnName === colName);
                                if (isFk && fkOptions[colName]) {
                                    return (
                                        <td key={colName} className="px-4 py-2">
                                            <select value={editFormData[colName] || ''} onChange={(e) => handleInputChange(e, colName)} className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2">
                                                <option value="">-- Select --</option>
                                                {fkOptions[colName].options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </td>
                                    );
                                }

                                return (
                                    <td key={colName} className="px-4 py-2">
                                        <input type="text" value={editFormData[colName] ?? ''} onChange={(e) => handleInputChange(e, colName)} className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded block w-full p-2" />
                                    </td>
                                );
                            })}
                        </tr>
                    );
                }

                return (
                    <TableRow 
                        key={rowKey}
                        row={row}
                        columns={visibleColumns}
                        pkCol={pkCol}
                        fks={fks}
                        fkOptions={fkOptions}
                        isPolymorphicTable={isPolymorphicTable}
                        polymorphicLabels={polymorphicLabels}
                        editingRowPk={editingRowPk}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        handleSaveClick={handleSaveClick}
                        handleCancelClick={handleCancelClick}
                        schemaWarning={schemaWarning}
                        isAdding={isAdding}
                    />
                );
            });
        }

        // It's a group object
        const groupKey = groupByConfig[depth];
        return Object.entries(dataNode).map(([groupValue, groupContent]) => {
            return (
                <CollapsibleGroup 
                    key={groupValue} 
                    groupKey={groupKey} 
                    groupValue={groupValue} 
                    colSpan={visibleColumns.length + 1}
                    depth={depth}
                >
                    {renderRowsOrGroups(groupContent, depth + 1)}
                </CollapsibleGroup>
            );
        });
    };

    return (
        <tbody>
            {renderAddRow()}
            {renderRowsOrGroups(processedData)}
        </tbody>
    );
}

function CollapsibleGroup({ groupKey, groupValue, colSpan, depth, children }) {
    const [expanded, setExpanded] = useState(true);

    // Cascading colors for deeper levels
    const bgColors = [
        'bg-slate-800',     // Depth 0
        'bg-slate-800/80',  // Depth 1
        'bg-slate-800/60',  // Depth 2
        'bg-slate-800/40'   // Depth 3+
    ];
    
    // Deeper levels get lighter borders
    const borderColors = [
        'border-blue-500/50',
        'border-indigo-500/50',
        'border-purple-500/50',
        'border-slate-500/50'
    ];
    
    const depthIdx = Math.min(depth, 3);
    const bgClass = bgColors[depthIdx];
    const borderClass = borderColors[depthIdx];
    
    // Dynamic padding instead of pixel margin to ensure it plays nice with table cells
    const paddingLeftClass = `pl-${(depth * 6) + 4}`;

    return (
        <>
            <tr 
                className={`${bgClass} border-l-4 ${borderClass} border-y border-y-slate-700/50 cursor-pointer hover:brightness-110 transition-all`}
                onClick={() => setExpanded(!expanded)}
            >
                <td colSpan={colSpan} className={`py-3 pr-4 font-semibold text-slate-200 ${paddingLeftClass}`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm text-slate-400">
                            {expanded ? 'expand_more' : 'chevron_right'}
                        </span>
                        <span className="text-lg tracking-wide text-white">
                            {groupValue === 'null' || groupValue === '' ? '(Empty)' : groupValue}
                        </span>
                    </div>
                </td>
            </tr>
            {expanded && children}
        </>
    );
}
